import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Edit,
  Trash2,
  Layers,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  delete_menu_data,
  get_all_veriation_res,
  save_variation,
  server_post_data,
  show_hide_menu,
} from "../ServiceConnection/serviceconnection";

export default function ItemVariations() {
  const { toast } = useToast();

  // State management
  const [variations, setVariations] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);

  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Form states
  const [formData, setFormData] = useState({
    internalName: "",
    externalName: "",
    variationType: "",
  });
  const [editingVariationId, setEditingVariationId] = useState(0);
  const [errors, setErrors] = useState({});

  // Handle form field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      internalName: "",
      externalName: "",
      variationType: "",
    });
    setErrors({});
    setEditingVariationId(0);
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.internalName.trim()) {
      newErrors.internalName = "Internal name is required";
    }

    if (!formData.externalName.trim()) {
      newErrors.externalName = "External name is required";
    }

    if (!formData.variationType) {
      newErrors.variationType = "Variation type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Fetch variations data
  const fetchVariations = useCallback(async () => {
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_all_veriation_res, fd);

      if (!response.data.error) {
        setVariations(response.data.message.data || []);
        setCategoryOptions(response.data.message.variation_category || []);
      } else {
        setVariations([]);
      }
    } catch (error) {
      console.error("Error fetching variations:", error);
      setVariations([]);
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, []);

  // Handle edit
  const handleEditClick = useCallback(
    async (variation) => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("flag", 2);
      fd.append("call_id", variation.primary_id);

      try {
        const response = await server_post_data(get_all_veriation_res, fd);

        if (!response.data.error && response.data.message.data.length > 0) {
          const data = response.data.message.data[0];
          setFormData({
            internalName: data.variation_name || "",
            externalName: data.variation_description || "",
            variationType: data.variation_type || "",
          });
          setEditingVariationId(data.primary_id);
          setErrors({});
        }
      } catch (error) {
        console.error("Error fetching variation:", error);
        toast({
          title: "Error",
          description: "Network error. Please try again.",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast],
  );

  // Handle save/update
  const handleSaveChanges = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("variation_type", formData.variationType);
    fd.append("variation_name", formData.internalName.trim());
    fd.append("variation_description", formData.externalName.trim());
    fd.append("main_id", editingVariationId);

    try {
      const response = await server_post_data(save_variation, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save variation",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editingVariationId
            ? "Variation updated successfully"
            : "Variation created successfully",
        });
        fetchVariations();
        resetForm();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    validateForm,
    formData,
    editingVariationId,
    toast,
    fetchVariations,
    resetForm,
  ]);

  // Handle delete
  const handleDeleteClick = useCallback((variation) => {
    setSelectedVariation(variation);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedVariation) return;

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag_for", 3);
    fd.append("id_for_delete", selectedVariation.primary_id);

    try {
      const response = await server_post_data(delete_menu_data, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete variation",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Deleted",
          description: `${selectedVariation.variation_name} has been deleted successfully`,
        });
        fetchVariations();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedVariation(null);
    }
  }, [selectedVariation, toast, fetchVariations]);

  // Handle toggle status
  const handleToggleClick = useCallback((variation) => {
    setSelectedVariation(variation);
    setStatusChangeMessage(
      variation.active_status
        ? "Are you sure you want to deactivate this variation?"
        : "Are you sure you want to activate this variation?",
    );
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedVariation) return;

    setShowLoaderAdmin(true);
    // const newStatus = !selectedVariation.active_status;
    const newStatus = !selectedVariation.active_status ? 0 : 1;

    const fd = new FormData();
    fd.append("item_id", selectedVariation.primary_id);
    fd.append("current_status", newStatus);
    fd.append("flag_for", "variation");

    try {
      const response = await server_post_data(show_hide_menu, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update status",
          variant: "destructive",
        });
      } else {
        setVariations((prev) =>
          prev.map((item) =>
            item.primary_id === selectedVariation.primary_id
              ? { ...item, active_status: newStatus }
              : item,
          ),
        );
        toast({
          title: "Success",
          description: `Variation ${
            newStatus ? "activated" : "deactivated"
          } successfully`,
        });
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedVariation(null);
    }
  }, [selectedVariation, toast]);

  // Initial load
  useEffect(() => {
    fetchVariations();
  }, [fetchVariations]);

  return (
    <MainLayout>
      <Header
        title="Item Variations Management"
        subtitle="Manage menu item variations and options"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Layers className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {variations.length}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Variations
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {variations.filter((v) => v.active_status).length}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <XCircle className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {variations.filter((v) => !v.active_status).length}
                </p>
                <p className="text-sm text-muted-foreground">Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Form Card */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {editingVariationId ? "Edit Variation" : "Add New Variation"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* Internal Name */}
            <div className="space-y-2">
              <Label htmlFor="internalName">
                Internal Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="internalName"
                placeholder="e.g., Small, Medium, Large"
                value={formData.internalName}
                onChange={(e) =>
                  handleFieldChange("internalName", e.target.value)
                }
                className={errors.internalName ? "border-destructive" : ""}
              />
              {errors.internalName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.internalName}
                </p>
              )}
            </div>

            {/* External Name */}
            <div className="space-y-2">
              <Label htmlFor="externalName">
                External Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="externalName"
                placeholder="e.g., S, M, L"
                value={formData.externalName}
                onChange={(e) =>
                  handleFieldChange("externalName", e.target.value)
                }
                className={errors.externalName ? "border-destructive" : ""}
              />
              {errors.externalName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.externalName}
                </p>
              )}
            </div>

            {/* Variation Type */}
            <div className="space-y-2">
              <Label htmlFor="variationType">
                Variation Unit <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.variationType}
                onValueChange={(value) =>
                  handleFieldChange("variationType", value)
                }
              >
                <SelectTrigger
                  className={errors.variationType ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select Unit" />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((option, index) => (
                    <SelectItem key={index} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.variationType && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.variationType}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleSaveChanges}
                disabled={showLoaderAdmin}
                className="flex-1 gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? (
                  "Saving..."
                ) : editingVariationId ? (
                  <>
                    <Edit className="w-4 h-4 mr-2" />
                    Update
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </>
                )}
              </Button>
              {editingVariationId > 0 && (
                <Button variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Variations Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Sr.No</TableHead>
                <TableHead className="text-muted-foreground">
                  Internal Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  External Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Variation Type
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-12 text-muted-foreground"
                  >
                    Loading Variations...
                  </TableCell>
                </TableRow>
              ) : variations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Layers className="w-8 h-8" />
                      <p>No variations found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                variations.map((variation, index) => (
                  <TableRow
                    key={variation.primary_id}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                          <Layers className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <p className="font-medium text-foreground">
                          {variation.variation_name}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {variation.variation_description}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {variation.variation_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleClick(variation)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                          variation.active_status
                            ? "bg-success"
                            : "bg-muted-foreground/30",
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            variation.active_status
                              ? "translate-x-6"
                              : "translate-x-1",
                          )}
                        />
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(variation)}
                          className="hover:bg-primary/10 hover:text-primary"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(variation)}
                          className="hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Variation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "
                <strong>{selectedVariation?.variation_name}</strong>"? This
                action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={showLoaderAdmin}
                className="bg-destructive hover:bg-destructive/90"
              >
                {showLoaderAdmin ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Toggle Status Confirmation Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Variation Status</AlertDialogTitle>
              <AlertDialogDescription>
                {statusChangeMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmToggle}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? "Updating..." : "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
